/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.08.02 at 18:54
 */
import { isArray, isPlainObject } from '@labor-digital/helferlein';
var Translator = /** @class */ (function () {
    function Translator(lang, context) {
        this._lang = lang;
        this._context = context;
    }
    Object.defineProperty(Translator.prototype, "lang", {
        /**
         * Returns the current language code (iso code) that is configured for this bit
         */
        get: function () {
            return this._lang;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Translator.prototype, "langShort", {
        /**
         * Returns the language code (iso two char code) that is configured for this bit
         */
        get: function () {
            return this.lang.substr(0, 2).toLowerCase();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Translator.prototype, "fallbackLang", {
        /**
         * Returns the configured fallback language code
         */
        get: function () {
            return this._context.fallbackLang;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Translates a single key using the loaded labels and returns the matched value.
     * @param key The label key to use for translation
     * @param args An array of arguments to replace using sprintf in your label
     * @param options Advanced translation options
     */
    Translator.prototype.translate = function (key, args, options) {
        var _a, _b, _c;
        options = options !== null && options !== void 0 ? options : {};
        var lang = (_a = options.lang) !== null && _a !== void 0 ? _a : this._lang;
        var labels = this._context.getPhrasesFor(lang);
        if (!labels || !labels[key]) {
            labels = this._context.getPhrasesFor(this.fallbackLang);
        }
        if (!labels || !labels[key]) {
            console.warn('Missing translation for key: "' + key + '" in language: "' + lang + '"');
            return key;
        }
        var argsIsObject = isPlainObject(args);
        var label = labels[key];
        if (isArray(label)) {
            var count = (_b = options.count) !== null && _b !== void 0 ? _b : (argsIsObject ? args.count : 1);
            var index = this._context.getPluralIndex(lang, count);
            if (!label[index]) {
                console.warn('Missing pluralization index ' + index + ' for key: "' + key + '" in language: "' + lang + '"');
                label = (_c = label[0]) !== null && _c !== void 0 ? _c : key;
            }
            else {
                label = label[index];
            }
            if (!argsIsObject) {
                label = this.replaceMarker(label, { count: count });
            }
        }
        if (args) {
            if (isArray(args)) {
                label = this.sprintf(label, args);
            }
            else if (argsIsObject) {
                label = this.replaceMarker(label, args);
            }
        }
        return label;
    };
    /**
     * Allows you to add additional phrases for a certain language.
     * Note: This affects ALL bit instances of this app!
     * @param lang The language code to add the phrases to
     * @param phrases The phrases to add
     */
    Translator.prototype.addPhrases = function (lang, phrases) {
        this._context.addPhrases(lang, phrases);
        return this;
    };
    /**
     * Allows you to set a new plural rule to apply for a certain language.
     * Note: This affects ALL bit instances of this app!
     * @param lang The language code to set the pluralization rule for
     * @param rule The rule to select the correct translation index with
     */
    Translator.prototype.addPluralRule = function (lang, rule) {
        this._context.addPluralRule(lang, rule);
        return this;
    };
    /**
     * Internal helper to perform a sprintf equivalent. %s and %d are supported.
     *
     * @param value The text to replace the arguments in
     * @param args The list of arguments to apply into the placeholders
     * @protected
     * @see https://www.php.net/manual/en/function.sprintf.php
     */
    Translator.prototype.sprintf = function (value, args) {
        var i = 0;
        return value.replace(/%([ds])/g, function (a, b) {
            if (args.length - 1 < i) {
                return a;
            }
            var v = b === 'd' ? parseInt(args[i] + '') + '' : args[i] + '';
            i++;
            return v;
        });
    };
    /**
     * Internal helper to replace markers with their matching value
     * @param value The text to replace the arguments in
     * @param args The argument object to replace on the value
     * @protected
     */
    Translator.prototype.replaceMarker = function (value, args) {
        return value.replace(/{{\s*(\w+)\s*}}/g, function (match, param) {
            var _a;
            return (_a = args[param]) !== null && _a !== void 0 ? _a : match;
        });
    };
    return Translator;
}());
export { Translator };
//# sourceMappingURL=Translator.js.map